import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ApiContext } from '@api/api';
import {
  DuplicationJobDetailOutDTO,
  DuplicationJobItemOutDTO,
  DuplicationJobItemsFilter,
  StateFilterEnum,
  ItemStatusEnum,
  JobStatusEnum,
  JobTypeEnum,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { Link } from 'react-router-dom';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import COText from '@app/pages/COText/COText.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import JobDetailHeader, { JobExpiryNote } from '@components/ringil3/Features/Job/JobDetailHeader.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import SupplierNotificationBadge from '@components/RingilComponents/badges/SupplierNotificationBadge.tsx';
import SvgOK from '@icons/check-circle.svg?react';
import SvgAlert from '@icons/alert-circle.svg?react';
import SvgWarning from '@icons/alert.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import SvgOrder from '@icons/order.svg?react';
import SvgUser from '@icons/user-inverted.svg?react';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import { mapFromAPIToDateShort } from '@utils/date.tsx';
import { isNotNullOrUndefined, useLocalStorage } from '@utils/utils';
import { formatJobItemError } from '@hooks/useBEValidation/formatBEValidationMessage';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { useJobEvents, useJobProgress } from '@hooks/useJobs/useJobs.ts';
import { InfoTagEnum } from '@components/ringil3/Features/Job/MultiduplicationV3/multiduplicationV3Table.ts';

interface ComponentProps {
  jobId: string;
}

const TERMINAL: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];

const initialExpandedInfoTags: Record<InfoTagEnum, boolean> = Object.values(InfoTagEnum).reduce(
  (acc, key) => ({ ...acc, [key]: false }),
  {} as Record<InfoTagEnum, boolean>,
);

/**
 * Job-type-specific detail for EXPEDITION_MULTI_DUPLICATION. Job-level data comes from ONE GET
 * (+ reconciliation GET when the job finishes, push counters via useJobProgress); the item table
 * is a classic RingilDataTable paged/filtered on the BE, reloaded (debounced) on item push events.
 */
const ExpeditionMultiDuplicationJobDetail: React.FC<ComponentProps> = ({ jobId }) => {
  const { getDuplicationJobDetail, getDuplicationJobItems, cancelAsyncJob, retryJob, archiveJob } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const [detail, setDetail] = useState<DuplicationJobDetailOutDTO>();
  // Key of the job action in flight (RD-2954). The clicked button spins (RingilButton swallows clicks
  // while `loading`) and the other action buttons grey out, so neither a double click nor a parallel
  // action sends a second request — that used to come back from the BE as a 409.
  // The key is cleared only once the detail GET that follows the mutation has landed: on a fast
  // (local) backend the POST can return before the second click of a double click, and until the
  // refetched status re-renders the page the old button is still there and would fire again.
  const [action, setAction] = useState<string | null>(null);
  const busy = (key: string) => ({ loading: action === key, disabled: action != null && action !== key });
  const detailRef = useRef<DuplicationJobDetailOutDTO | undefined>(undefined);
  detailRef.current = detail;
  const abortControllerRef = useRef<AbortController | null>(null);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: DuplicationJobItemOutDTO[]; clear: () => void }>(null);
  const reloadTimer = useRef<number | null>(null);

  const jobsRwFlag = useSecuredFlag('jobs-rw-actions', {
    kind: 'action',
    description: 'Retry / archive jobs',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_RW],
  });

  const [expandedInfoTags, setExpandedInfoTags] = useLocalStorage<Record<InfoTagEnum, boolean>>(
    'MULTIDUPLICATION_JOB_EXPANDED_INFO_TAGS',
    initialExpandedInfoTags,
  );
  const [expandedLabels, setExpandedLabels] = useLocalStorage('MULTIDUPLICATION_JOB_EXPANDED_LABELS', false);

  const toggleInfoTag = (key: InfoTagEnum) => setExpandedInfoTags({ ...expandedInfoTags, [key]: !expandedInfoTags[key] });
  const toggleAllInfoTags = () => {
    const expanded = !expandedInfoTags[InfoTagEnum.ALL];
    setExpandedInfoTags(Object.values(InfoTagEnum).reduce((acc, key) => ({ ...acc, [key]: expanded }), {} as Record<InfoTagEnum, boolean>));
  };
  const toggleLabels = () => setExpandedLabels(!expandedLabels);

  // Live counters/status from the push-fed store; fresher than the fetched detail during the run
  const live = useJobProgress(jobId);

  const load = (onLoaded?: () => void) => {
    getDuplicationJobDetail(jobId, d => {
      if (abortControllerRef.current?.signal.aborted) return;
      setDetail(d);
      onLoaded?.();
    });
  };

  // 1st GET on mount
  useEffect(() => {
    const ac = new AbortController();
    abortControllerRef.current = ac;
    load();
    return () => ac.abort();
  }, [jobId]);

  useEffect(() => () => {
    if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
  }, []);

  // Item push events reload the paged table (debounced — bursts of per-date results while running);
  // final GET on terminal transition refreshes the job-level card too.
  const debouncedTableReload = () => {
    if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => tableRef.current?.reloadData(), 400);
  };

  useJobEvents(jobId, {
    onItem: () => debouncedTableReload(),
    onFinished: () => {
      const current = detailRef.current;
      // Same as the export detail: a null detail means the initial load is still in flight.
      if (current != null && !TERMINAL.includes(current.job.jobStatus)) load();
      debouncedTableReload();
    },
    onReconnect: () => {
      load();
      debouncedTableReload();
    },
  });

  const loadDataApi = (filter: DuplicationJobItemsFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<DuplicationJobItemOutDTO>) => void) => {
    getDuplicationJobItems(jobId, filter, pageable, data => onSuccess(data));
  };

  const stateLabel = (state: StateFilterEnum) =>
    ({
      [StateFilterEnum.WITH_ERROR]: tr('JobDetail.duplication.state.WITH_ERROR', 'S chybou / kolizí'),
      [StateFilterEnum.OK]: tr('JobDetail.duplication.state.OK', 'V pořádku'),
    })[state];

  const statusIcon = (row: DuplicationJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.DONE && isNotNullOrUndefined(row.expeditionId)) return <SvgOK className={'text-complementary-success shrink-0'} />;
    if (row.itemStatus === ItemStatusEnum.ERROR) return <SvgAlert className={'text-complementary-error shrink-0'} />;
    if (row.itemStatus === ItemStatusEnum.RUNNING) return <SvgLoader className={'animate-spin shrink-0'} />;
    return null;
  };

  const renderRouteColumn = (row: DuplicationJobItemOutDTO, toggleExpandedRow?: (row: DuplicationJobItemOutDTO) => void) => {
    // Route from the created expedition's locations; source route as fallback for failed rows
    const routeNames = row.locations.length > 0 ? row.locations.map(l => l.locationName) : (detail?.source?.routeSummary ?? []);
    const stopCount = Math.max(routeNames.length - 2, 0);
    const hasReservationInfo = row.locations.some(l => l.reservationCreated != null);
    return (
      <div className={'flex flex-row gap-2 items-start'}>
        {statusIcon(row)}
        <div className={'flex flex-col gap-1'}>
          <COBadge variant={'outlined'} type={'neutral'}>
            <span className={'text-content-secondary'}>{tr('MultiduplicationJobTable.rowId', 'ID:')}</span>
            &nbsp;{row.sortOrder + 1}
            {row.date != null && <>&nbsp;·&nbsp;{mapFromAPIToDateShort(row.date)}</>}
          </COBadge>
          {routeNames.length > 0 && (
            <>
              <span className={'text-content-primary'}>{routeNames[0]}</span>
              {routeNames.length > 1 && <span className={'text-content-primary'}>{routeNames[routeNames.length - 1]}</span>}
              {stopCount > 0 && (
                <span className={'text-sm text-content-tertiary'}>
                  {tr('MultiduplicationV3.intermediateStops', '+ {{count}} mezizastávky', { count: stopCount }, stopCount)}
                </span>
              )}
            </>
          )}
          {hasReservationInfo && (
            <span
              className={'flex flex-row gap-1 items-center cursor-pointer text-content-brand'}
              onClick={() => toggleExpandedRow?.(row)}
              data-testid={`multiduplication-job-row-${row.sortOrder}-expand`}
            >
              {tr('MultiduplicationV3.expand', 'Rozbalit')}
              <SvgChevron className={'text-content-brand'} />
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderCreatedColumn = (row: DuplicationJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.DONE && isNotNullOrUndefined(row.expeditionId)) {
      return (
        <div className={'flex flex-col gap-1 items-start'}>
          <div className={'flex flex-row gap-2 items-center'}>
            <Link className={'text-content-brand font-medium'} to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(row.expeditionId))}>
              {row.expeditionApplicationId}
            </Link>
            {row.someReservationFailed && (
              <span title={tr('DuplicateExpeditionSidebar.someReservationsFailed', 'Some reservations could not be created.')}>
                <SvgWarning className={'text-complementary-warning'} />
              </span>
            )}
          </div>
          {detail?.createdByName != null && <span className={'text-sm text-content-tertiary'}>{detail.createdByName}</span>}
          {detail?.source?.supplierNotificationId != null && (
            <SupplierNotificationBadge
              supplierNotificationId={detail.source.supplierNotificationId}
              supplierNotificationAppId={detail.source.supplierNotificationAppId}
            />
          )}
          {(row.labels ?? []).length > 0 && <ExpandableLabelWrapper labels={row.labels} isExpanded={expandedLabels} onToggle={toggleLabels} />}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.ERROR) {
      const errorText = formatJobItemError(row.error, row.errorMessage, tr);
      return (
        <div className={'flex flex-col gap-1'}>
          <COText label={tr('JobDetail.item.notCreated', 'Not created')} />
          {isNotNullOrUndefined(errorText) && <span className={'text-content-tertiary text-sm'}>{errorText}</span>}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.RUNNING) {
      return <COText label={tr('JobDetail.item.inProgress', 'In progress')} />;
    }
    if (row.itemStatus === ItemStatusEnum.SKIPPED) {
      return <span className={'text-content-tertiary'}>{tr('JobDetail.item.skipped', 'Skipped')}</span>;
    }
    return <COText label={tr('JobDetail.item.waiting', 'Waiting')} />;
  };

  const renderSourceColumn = () => {
    const source = detail?.source;
    if (source == null) return <div />;
    return (
      <div className={'flex flex-col gap-1'}>
        <Link to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(source.expeditionId))}>
          {source.applicationId ?? tr('MultiduplicationV3.sourceExpedition', 'Výchozí přeprava')}
        </Link>
        {(source.labels ?? []).length > 0 && <ExpandableLabelWrapper labels={source.labels} isExpanded={expandedLabels} onToggle={toggleLabels} />}
      </div>
    );
  };

  const renderInfoTagsColumn = (row: DuplicationJobItemOutDTO) => {
    const expeditionType = row.expeditionType == null ? null : tr(`global.expeditionTypeShort${row.expeditionType}`, `${row.expeditionType}`);
    return (
      <div className={'flex flex-wrap gap-2 items-start'}>
        {(expeditionType != null || isNotNullOrUndefined(row.responsiblePerson) || (row.orderCount ?? 0) > 0) && (
          <ExpandableInfoAllTag
            id={`all_${row.id}`}
            onClick={toggleAllInfoTags}
            tooltipText={expandedInfoTags[InfoTagEnum.ALL] ? tr(`expandableInfoTag.collapseAll`, 'Collapse all') : tr(`expandableInfoTag.expandAll`, 'Expand all')}
            icon={<SvgChevron className={clsx('text-content-brand', expandedInfoTags[InfoTagEnum.ALL] && 'rotate-180')} />}
          />
        )}
        {expeditionType != null && (
          <ExpandableInfoTag
            id={`type_${row.id}`}
            expanded={expandedInfoTags[InfoTagEnum.TYPE]}
            onClick={() => toggleInfoTag(InfoTagEnum.TYPE)}
            iconText={expeditionType}
          />
        )}
        {isNotNullOrUndefined(row.responsiblePerson) && (
          <ExpandableInfoTag
            id={`responsible_${row.id}`}
            expanded={expandedInfoTags[InfoTagEnum.RESPONSIBLE_PERSON]}
            onClick={() => toggleInfoTag(InfoTagEnum.RESPONSIBLE_PERSON)}
            label={tr('MultiduplicationV3.responsiblePersonLabel', 'Odpovědná osoba: ')}
            text={row.responsiblePerson}
            icon={<SvgUser />}
          />
        )}
        {(row.orderCount ?? 0) > 0 && (
          <ExpandableInfoTag
            id={`orders_${row.id}`}
            expanded={expandedInfoTags[InfoTagEnum.ORDERS]}
            onClick={() => toggleInfoTag(InfoTagEnum.ORDERS)}
            label={tr('AllExpedition.ordersTagLabel', 'Orders: ')}
            text={`${row.orderCount}`}
            icon={<SvgOrder />}
            count={row.orderCount}
          />
        )}
      </div>
    );
  };

  const rowExpansionTemplate = (row: DuplicationJobItemOutDTO) => {
    const withReservation = row.locations.filter(l => l.reservationCreated != null);
    return (
      <div className={'flex flex-col gap-1 px-4 py-2'}>
        {withReservation.map((loc, i) => (
          <div key={i} className={'flex flex-row gap-2 items-center'}>
            <span className={'text-content-secondary'}>{loc.locationName}:</span>
            {loc.reservationCreated === true && isNotNullOrUndefined(loc.reservationId) && (
              <>
                <SvgOK className={'text-complementary-success'} />
                <Link to={nav.createNavigationLink(nav.urlFunctions.createReservationDetail(loc.reservationId))}>{loc.reservationApplicationId}</Link>
                {loc.hasNoSlots === true && (
                  <span title={tr('DuplicateExpeditionSidebar.reservationWithoutTimeSlot', 'The reservation does not have a time slot')}>
                    <SvgWarning className={'text-complementary-warning'} />
                  </span>
                )}
              </>
            )}
            {loc.reservationCreated === false && (
              <>
                <SvgAlert className={'text-complementary-error'} />
                <COText label={tr('DuplicateExpeditionSidebar.notCreated', 'Not created')} />
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  const columnConfig: ColumnConfig<DuplicationJobItemOutDTO>[] = [
    { id: 'route', header: 'MultiduplicationV3.table.route', headerDefault: 'Trasa', width: '30%', order: 1, render: renderRouteColumn },
    { id: 'created', header: '', headerDefault: '', width: '30%', order: 2, render: renderCreatedColumn },
    { id: 'duplicatedFrom', header: 'MultiduplicationV3.table.duplicatedFrom', headerDefault: 'Zduplikováno z', width: '20%', order: 3, render: renderSourceColumn },
    { id: 'additionalInfo', header: 'MultiduplicationV3.table.additionalInfo', headerDefault: 'Další informace', width: '20%', order: 4, render: renderInfoTagsColumn },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('JobDetail.duplication.filterState', 'Status'),
      label: tr('JobDetail.duplication.filterState', 'Status'),
      value: ['stateFilter'],
      options: Object.values(StateFilterEnum).map(state => ({ label: stateLabel(state), value: state, active: true })),
      dataTestId: 'duplication-job-filter-state',
    },
  ];

  if (detail == null) {
    return <SvgLoader className={'animate-spin'} />;
  }

  // Live snapshot wins during the run; fetched detail is authoritative once terminal
  const detailTerminal = TERMINAL.includes(detail.job.jobStatus);
  const status = detailTerminal ? detail.job.jobStatus : ((live?.status as JobStatusEnum | undefined) ?? detail.job.jobStatus);
  const processed = detailTerminal ? detail.job.processedCount : live?.processed ?? detail.job.processedCount;
  const failed = detailTerminal ? detail.job.failedCount : live?.failed ?? detail.job.failedCount;
  const total = detailTerminal ? detail.job.totalCount : live?.total ?? detail.job.totalCount;
  const applicationId = detail.job.applicationId ?? live?.applicationId;
  const isRunning = !TERMINAL.includes(status);
  const isTerminal = !isRunning;
  const progressPct = total != null && total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;
  const canRetry = status === JobStatusEnum.ERROR || (status === JobStatusEnum.DONE && failed > 0);
  const isAllOk = status === JobStatusEnum.DONE && failed === 0 && !detail.someReservationFailed;
  const title = `${tr('JobDetail.task', 'Úkol')} ${applicationId ?? ''}`;

  const retry = () => {
    setAction('retry');
    retryJob(
      jobId,
      () => load(() => setAction(null)),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const cancel = () => {
    setAction('cancel');
    cancelAsyncJob(
      jobId,
      () => load(() => setAction(null)),
      null,
      {
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const archive = () => {
    setAction('archive');
    archiveJob(jobId, () => nav.navigate(nav.urlFunctions.createJobList()), null, {
      successToast: false,
      onError: (_e, showStandardError) => {
        setAction(null);
        showStandardError();
      },
    });
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <BreadCrumbComponent
        items={[
          { label: tr('JobList.title', 'Rozpracované úkoly'), url: nav.createNavigationLink(nav.urlFunctions.createJobList()) },
          { label: title, disabled: true },
        ]}
      />

      <JobDetailHeader
        title={title}
        jobTypeLabel={tr(`JobList.typeBadge.${JobTypeEnum.EXPEDITION_MULTI_DUPLICATION}`, 'Duplikace')}
        jobCategory={detail.jobCategory}
        createdByName={detail.createdByName}
        createdAt={detail.job.createdAt}
        onCancel={isRunning ? cancel : undefined}
        onArchive={isTerminal && jobsRwFlag.granted ? archive : undefined}
        busyAction={action}
      />

      {isRunning && (
        <div className={'flex flex-col gap-4'}>
          <COInlineMessage variant={'warning'} contentClassName={'flex-1 text-center'}>
            {tr('JobDetail.duplication.inProgress', 'Přepravy se zpracovávají')}
          </COInlineMessage>
          <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
            <SvgLoader className={'animate-spin'} />
            <span className={'text-content-secondary'}>{tr('JobList.processedPct', '{{pct}} % zpracováno', { pct: progressPct })}</span>
            <div className={'w-60 bg-base-quaternary rounded h-2'}>
              <div className={'bg-content-brand h-2 rounded'} style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>
      )}

      {isAllOk && (
        <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
          <div className={'flex flex-row items-center gap-3'}>
            <SvgOK className={'text-complementary-success w-8 h-8'} />
            <span>{tr('JobDetail.duplication.done', 'Duplikace přeprav je hotová')}</span>
          </div>
          <RingilButton
            mode={'filled-rectangle'}
            label={tr('JobDetail.duplication.showCreated', 'Zobrazit všechny vytvořené přepravy v přepravách')}
            dataTestId={'multiduplication-job-show-created'}
            onClick={() => nav.navigate(nav.urlFunctions.createCustomerExpeditionList())}
          />
        </div>
      )}

      {status === JobStatusEnum.ERROR && (
        <COInlineMessage variant={'error'} contentClassName={'flex-1 text-center'}>
          <div>{tr('JobDetail.duplication.failed', 'Přepravy se nepodařilo zduplikovat.')}</div>
          {detail.job.errorMessage != null && <div>{detail.job.errorMessage}</div>}
        </COInlineMessage>
      )}
      {status === JobStatusEnum.DONE && failed > 0 && (
        <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('JobDetail.someFailed', 'Some transports could not be created. See the details below.')}
        </COInlineMessage>
      )}
      {status === JobStatusEnum.CANCELLED && (
        <COInlineMessage variant={'info'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('JobDetail.cancelled', 'The job was cancelled. Transports created before the cancellation remain.')}
        </COInlineMessage>
      )}
      {detail.someReservationFailed && (
        <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('DuplicateExpeditionSidebar.someReservationsFailed', 'Some reservations could not be created.')}
        </COInlineMessage>
      )}

      {canRetry && jobsRwFlag.granted && (
        <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
          <RingilButton
            mode={'filled-rectangle'}
            label={tr('JobList.retry', 'Zkusit znovu')}
            dataTestId={'multiduplication-job-retry'}
            onClick={retry}
            {...busy('retry')}
          />
        </div>
      )}

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'duplication_job_items'}
        rowExpansionTemplate={rowExpansionTemplate}
      />

      {isTerminal && <JobExpiryNote expiresAt={detail.expiresAt} />}
    </div>
  );
};

export default ExpeditionMultiDuplicationJobDetail;
